import { SOURCE_MANUAL, SOURCE_TEAMS, TYPE_FOLDER } from "util/constants";
import { DocumentData } from "util/interfaces/DocumentData";

export const performDownload = (content: Blob, fileType: string, fileName?: string) => {
  const link = document.createElement("a");
  link.href = globalThis.URL.createObjectURL(content);
  link.download = fileName ?? `KB_files_${getCurrentDateMMDDYYYY_HHMM()}.${fileType}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const getBlobTree = (blobs: DocumentData[]) => {
  //Let's loop the blob list
  const tree: DocumentData[] = [];
  blobs.forEach((blob) => {
    let actualId: string | null = null;
    let actualPath: string = "";
    if (blob.path !== "") {
      //Let's split the path property
      const splittedPath = blob.path.split("/").filter((path) => path !== "");
      for (let index = 0; index < splittedPath.length; index++) {
        const folder = splittedPath[index];
        //Lets find if the actual folder exists
        actualPath += (actualPath === "" ? "" : "/") + folder;
        const id: string = `${actualId}-${folder}-${index}`;
        const folderExists = tree.find((aF) => aF.id === id);
        if (!folderExists) {
          //If not exists, let's add it
          const folderToCreate = {
            documentName: folder,
            file_name: folder,
            indexing_status: "SUCCESS" as const,
            fileType: TYPE_FOLDER,
            category: blob.category,
            id: id,
            metadata: "",
            path: actualPath,
            previewUrl: "",
            uploaded: blob.uploaded,
            uploadedBy: blob.uploadedBy,
            url: "",
            child: [],
            fileAlias: "",
            folderSource: "",
            source: "",
            file_extension: `1.${TYPE_FOLDER}`,
            uuid: id,
            parentId: actualId,
          };
          tree.push(folderToCreate);
        } else if (!folderExists.category.includes(blob.category)) {
          folderExists.category += " | " + blob.category;
        }
        //Let's add the blob to the folder
        actualId = id;
      }
    }
    tree.push({ ...blob, parentId: actualId });
  });
  //Lets check the source of the files

  for (const t of tree
    .filter((t) => t.fileType === TYPE_FOLDER && t.source === "")
    .sort((a, b) => {
      // Si a tiene parentId null y b no, a va al final (retorna 1)
      if (a.parentId === null && b.parentId !== null) return 1;
      // Si b tiene parentId null y a no, b va al final (retorna -1)
      if (b.parentId === null && a.parentId !== null) return -1;
      // Si ambos tienen parentId null, mantener orden
      if (a.parentId === null && b.parentId === null) return 0;
      // Si ambos tienen parentId no null, usar el ordenamiento original
      return (a.parentId || "") < (b.parentId || "") ? 1 : -1;
    })) {
    //We need to get all the children of this folder
    const children = tree.filter((c) => c.parentId === t.id);
    t.source = SOURCE_MANUAL;
    if (children.some((c) => c.source === SOURCE_TEAMS)) {
      t.source = SOURCE_TEAMS;
    }
  }

  return tree.sort((a, b) => {
    if (a.fileType === TYPE_FOLDER && b.fileType !== TYPE_FOLDER) return -1;
    if (a.fileType !== TYPE_FOLDER && b.fileType === TYPE_FOLDER) return 1;
    return a.documentName.localeCompare(b.documentName);
  });
};
export type DocumentField = keyof DocumentData;
export interface FilteredParams {
  searchText: string;
  selectedField: DocumentField | "";
  selectedValues: string[];
  sortOrder: "ascending" | "descending" | "";
}

export const getChilds = (
  parentDocument: DocumentData,
  allDocuments: DocumentData[]
): DocumentData[] => {
  const children: DocumentData[] = [];

  // Find all direct children
  const directChildren = allDocuments.filter((doc) => doc.parentId === parentDocument.id);

  // Add direct children and recursively get their children
  for (const child of directChildren) {
    children.push(child);
    // Recursively get children of this child
    const grandChildren = getChilds(child, allDocuments);
    children.push(...grandChildren);
  }

  return children;
};

const addDocumentWithChildren = (
  doc: DocumentData,
  allDocuments: DocumentData[],
  result: DocumentData[],
  processedIds: Set<string>
): void => {
  result.push(doc);
  processedIds.add(doc.id);

  if (doc.fileType !== TYPE_FOLDER) return;

  for (const child of getChilds(doc, allDocuments)) {
    if (!processedIds.has(child.id)) {
      result.push(child);
      processedIds.add(child.id);
    }
  }
};

/**
 * Gets documents by IDs and recursively includes all children if the document is a folder
 * @param ids - Array of document IDs to retrieve
 * @param allDocuments - Array of all available documents
 * @returns Array of documents including the requested documents and all their descendants
 */
export const getDocumentsWithChildren = (
  ids: string[],
  allDocuments: DocumentData[]
): DocumentData[] => {
  const result: DocumentData[] = [];
  const processedIds = new Set<string>();

  for (const id of ids) {
    const doc = allDocuments.find((d) => d.id === id);
    if (doc && !processedIds.has(doc.id)) {
      addDocumentWithChildren(doc, allDocuments, result, processedIds);
    }
  }

  return result;
};

export const getCurrentDateMMDDYYYY_HHMM = () => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const yyyy = now.getFullYear();
  const hh = String(now.getHours()).padStart(2, "0");
  const min = String(now.getMinutes()).padStart(2, "0");
  return `${mm}${dd}${yyyy}_${hh}${min}`;
};
